import { captureException } from '@sentry/react';
import { useQuery } from '@tanstack/react-query';

import { authorizedAxiosInstance } from '@/clients';

export const fetchSourceDocument = async (url: string, signal?: AbortSignal): Promise<string> => {
    try {
        const parsedUrl = new URL(url);
        if (!['http:', 'https:'].includes(parsedUrl.protocol)) throw new Error('Unsupported source URL.');
        const isSec = parsedUrl.hostname === 'sec.gov' || parsedUrl.hostname.endsWith('.sec.gov');
        const response = isSec
            ? await authorizedAxiosInstance.get<string>('/api/vendors/fmp/sec-filings/document', {
                  params: { url },
                  signal,
                  timeout: 30_000,
              })
            : await authorizedAxiosInstance.post<{ html: string }>(
                  '/api/services/metascraper/document',
                  { url },
                  {
                      signal,
                      timeout: 30_000,
                  },
              );
        const html = typeof response.data === 'string' ? response.data : response.data.html;
        if (typeof html !== 'string' || !html.trim()) throw new Error('Source document is empty.');
        return html;
    } catch (error) {
        if (!signal?.aborted) captureException(error, { tags: { action: 'load-source-document' } });
        throw error;
    }
};

export const useSourceDocument = (url?: string) =>
    useQuery({
        queryKey: ['source-document', url],
        queryFn: ({ signal }) => fetchSourceDocument(url!, signal),
        enabled: Boolean(url),
        retry: false,
        staleTime: 300_000,
        refetchOnWindowFocus: false,
    });
